<script>
  import { onMount } from "svelte";
  import Header from "./Header.svelte";
  import GenerationManager from "./GenerationManager.svelte";
  import PatTokenManager from "./PatTokenManager.svelte";
  import CatalogManager from "./CatalogManager.svelte";
  import ProjectManager from "./ProjectManager.svelte";
  import UserManager from "./UserManager.svelte";
  import LlmSettingsManager from "./LlmSettingsManager.svelte";
  import RulesPage from "./RulesPage.svelte";
  import HelpManager from "./HelpManager.svelte";

  export let isAdmin = false;
  export let userEmail = "";
  export let userRole = "";

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

  const sections = [
    { id: "generations", label: "Generations" },
    { id: "pat_tokens", label: "PAT Tokens" },
    ...(isAdmin ? [ { id: "projects", label: "Projects" }, { id: "catalog", label: "Catalog" }, { id: "users", label: "Users" }, { id: "settings/llm", label: "LLM Settings" } ] : []),
    // Help — visible to ALL roles, so it's outside the isAdmin spread. The menu
    // is English-only; Russian lives only inside the help ARTICLES.
    { id: "help", label: "Help" }
  ];

  let activeSection = "generations";

  // URL-owned selection state. The section managers own their loaded detail
  // objects; AppShell only tracks which id the URL points at. Each id-carrying
  // section keeps its own slot (generations -> a record, catalog -> a project).
  let selectedGenerationId = null;
  let selectedProjectId = null;
  // For generations only, the URL can also carry a selected run
  // (/generations/:id/runs/:runId). Owned here like selectedGenerationId; flows
  // to GenerationManager as a prop and back via the onSelectRun callback.
  let selectedRunId = null;
  // Part C (rules-editor redesign): /generations/:id/rules is a sub-view of the
  // generations section, mirroring the runId pattern above — a boolean flag
  // rather than an id since there's only one rules page per generation. When
  // true, AppShell renders RulesPage instead of GenerationManager's detail view
  // for the same selectedGenerationId. URL-addressable, Back-button-friendly,
  // deep-link/F5 safe (same applyLocation() path as everything else here).
  let rulesMode = false;
  // Rules create/edit sub-mode, URL-owned (mirrors the projects sub-mode below):
  //   /generations/:id/rules            -> rulesMode true, form closed
  //   /generations/:id/rules/new        -> rulesFormMode "new"
  //   /generations/:id/rules/:rid/edit  -> rulesFormMode "edit" + rulesEditRuleId
  // Flow to RulesPage as props so opening a form is a real history entry (Back
  // closes it) and each is deep-linkable / survives an F5.
  let rulesFormMode = null;   // null | "new" | "edit"
  let rulesEditRuleId = null; // number | null

  // Projects section sub-mode, URL-owned (mirrors the id-carrying sections):
  //   /projects                -> list (all null)
  //   /projects/new            -> projectMode "new"
  //   /projects/edit/:id       -> projectMode "edit" + projectEditId
  //   /projects/templates/new  -> templateMode "new"
  //   /projects/templates/edit/:id -> templateMode "edit" + templateEditId
  // Reflected to ProjectManager as props so a form open is a real history entry
  // (Back closes it) and is deep-linkable.
  let projectMode = null;    // null | "new" | "edit"
  let projectEditId = null;  // number | null
  let templateMode = null;   // null | "new" | "edit"
  let templateEditId = null; // number | null

  // Help section sub-state, URL-owned like selectedProjectId — the selected
  // topic slug (or null for the bare /help index/list view):
  //   /help          -> helpTopic null (HelpManager default-selects the first
  //                      topic once its manifest loads, but doesn't own the URL)
  //   /help/:topic   -> helpTopic the slug
  let helpTopic = null; // string | null

  // The id the URL should carry for a given section (null for sections that
  // don't deep-link). Keeps pathFor/navigate/selectSection in one place.
  function idForSection(sectionId) {
    if (sectionId === "generations") return selectedGenerationId;
    if (sectionId === "catalog") return selectedProjectId;
    if (sectionId === "help") return helpTopic;
    return null;
  }

  // --- URL <-> view state ------------------------------------------------
  // No SvelteKit here, so we drive navigation off the History API directly.
  // Paths mirror the Rails routes: "/generations", "/generations/:id",
  // "/pat_tokens", "/projects", "/catalog", "/catalog/:id", "/users",
  // "/help", "/help/:topic".

  function pathFor(sectionId, id, runId = null, rules = false) {
    if (sectionId === "help" && id) {
      return `/help/${id}`;
    }
    if ((sectionId === "generations" || sectionId === "catalog") && id) {
      // A run id / the rules sub-page only deep-link under generations, and
      // only alongside an id. rules and runId are mutually exclusive views of
      // the same generation; rules wins if somehow both are set.
      if (sectionId === "generations" && rules) {
        return `/generations/${id}/rules`;
      }
      if (sectionId === "generations" && runId) {
        return `/generations/${id}/runs/${runId}`;
      }
      return `/${sectionId}/${id}`;
    }
    return `/${sectionId}`;
  }

  // The canonical rules path for a generation given its form sub-mode.
  function rulesPathFor(id, formMode, ruleId) {
    if (formMode === "new") return `/generations/${id}/rules/new`;
    if (formMode === "edit" && ruleId) return `/generations/${id}/rules/${ruleId}/edit`;
    return `/generations/${id}/rules`;
  }

  // The canonical path for the projects section given its sub-mode.
  function projectsPathFor(mode, editId, tmode, teditId) {
    if (mode === "new") return "/projects/new";
    if (mode === "edit" && editId) return `/projects/edit/${editId}`;
    if (tmode === "new") return "/projects/templates/new";
    if (tmode === "edit" && teditId) return `/projects/templates/edit/${teditId}`;
    return "/projects";
  }

  // Build a parseLocation result with all fields defaulted, overriding a few.
  function loc(overrides) {
    return {
      section: "generations", id: null, runId: null, rulesMode: false,
      rulesFormMode: null, rulesEditRuleId: null,
      projectMode: null, projectEditId: null, templateMode: null, templateEditId: null,
      helpTopic: null,
      ...overrides
    };
  }

  // Low-level history push shared by navigate() and the projects form helpers.
  // popstate reads window.location (not this state object), so state is only a
  // convenience payload.
  function pushPath(path, state, replace = false) {
    if (path === window.location.pathname) {
      return;
    }
    window.history[replace ? "replaceState" : "pushState"](state, "", path);
  }

  // Sync component state to whatever the URL currently says. Used on first mount
  // and on every browser back/forward (popstate). Never pushes history itself.
  // The section managers load their detail reactively off the id prop.
  function parseLocation() {
    const [first, second, third, fourth, fifth] = window.location.pathname.split("/").filter(Boolean);
    if (!first || first === "generations") {
      const id = second ? Number(second) : null;
      const genId = Number.isInteger(id) ? id : null;
      // /generations/:id/runs/:runId — carry the run id too (generations only).
      let runId = null;
      if (genId && third === "runs" && fourth) {
        const parsedRun = Number(fourth);
        runId = Number.isInteger(parsedRun) ? parsedRun : null;
      }
      // /generations/:id/rules[...] — the Part C wide edit page, a sub-view like
      // runs. Its own create/edit sub-modes live one segment deeper:
      //   .../rules/new           -> rulesFormMode "new"
      //   .../rules/:ruleId/edit  -> rulesFormMode "edit" + rulesEditRuleId
      const rulesMode = !!(genId && third === "rules");
      let rulesFormMode = null;
      let rulesEditRuleId = null;
      if (rulesMode) {
        if (fourth === "new") {
          rulesFormMode = "new";
        } else if (fourth && fifth === "edit") {
          const rid = Number(fourth);
          if (Number.isInteger(rid)) { rulesFormMode = "edit"; rulesEditRuleId = rid; }
        }
      }
      return loc({ section: "generations", id: genId, runId, rulesMode, rulesFormMode, rulesEditRuleId });
    }
    // Multi-segment section ids (e.g. "settings/llm") match the leading path
    // segments verbatim; they never carry an id, so resolve them first.
    const joined = [first, second].filter(Boolean).join("/");
    if (sections.some((section) => section.id === joined)) {
      return loc({ section: joined });
    }
    // Only honour sections this user actually has (admin-only tabs are filtered
    // out of `sections`); anything else falls back to the default view.
    if (sections.some((section) => section.id === first)) {
      if (first === "catalog") {
        const id = second ? Number(second) : null;
        return loc({ section: "catalog", id: Number.isInteger(id) ? id : null });
      }
      if (first === "help") {
        // /help (index) | /help/:topic (a slug, not numeric — unlike catalog/:id).
        return loc({ section: "help", helpTopic: second || null });
      }
      if (first === "projects") {
        // /projects | /projects/new | /projects/edit/:id | /projects/templates/new
        // | /projects/templates/edit/:id
        if (second === "new") {
          return loc({ section: "projects", projectMode: "new" });
        }
        if (second === "edit" && third) {
          const pid = Number(third);
          return Number.isInteger(pid)
            ? loc({ section: "projects", projectMode: "edit", projectEditId: pid })
            : loc({ section: "projects" });
        }
        if (second === "templates" && third === "new") {
          return loc({ section: "projects", templateMode: "new" });
        }
        if (second === "templates" && third === "edit" && fourth) {
          const tid = Number(fourth);
          return Number.isInteger(tid)
            ? loc({ section: "projects", templateMode: "edit", templateEditId: tid })
            : loc({ section: "projects" });
        }
        return loc({ section: "projects" });
      }
      return loc({ section: first });
    }
    return loc({ section: "generations" });
  }

  function navigate(sectionId, id = null, { replace = false, runId = null, rules = false } = {}) {
    pushPath(pathFor(sectionId, id, runId, rules), { section: sectionId, id, runId, rules }, replace);
  }

  function applyLocation() {
    const { section, id, runId, rulesMode: rm, rulesFormMode: rfm, rulesEditRuleId: rerid, projectMode: pm, projectEditId: pe, templateMode: tm, templateEditId: te, helpTopic: ht } = parseLocation();
    activeSection = section;
    selectedGenerationId = section === "generations" ? id : null;
    selectedProjectId = section === "catalog" ? id : null;
    // A run id is only meaningful for the generations section and only when a
    // generation is actually open; otherwise there's no selected run.
    selectedRunId = section === "generations" && id ? runId : null;
    // Same for the rules sub-page (mutually exclusive with a selected run) and
    // its create/edit form sub-mode (only meaningful when the rules page is up).
    rulesMode = section === "generations" && id ? rm : false;
    rulesFormMode = rulesMode ? rfm : null;
    rulesEditRuleId = rulesMode ? rerid : null;
    // Projects sub-mode only applies within the projects section.
    projectMode = section === "projects" ? pm : null;
    projectEditId = section === "projects" ? pe : null;
    templateMode = section === "projects" ? tm : null;
    templateEditId = section === "projects" ? te : null;
    // Help sub-mode (selected topic slug) only applies within the help section.
    helpTopic = section === "help" ? ht : null;
  }

  function selectSection(sectionId) {
    // A header-tab click never carries a run id, so drop any URL-owned run
    // selection first. Otherwise the tab navigates to a run-less path (e.g.
    // /generations/:id) while GenerationManager re-mounts with a stale
    // selectedRunId prop and renders that run's archive — an address-bar ↔ view
    // divergence. Mirrors open/closeGeneration. A tab click also clears any
    // projects sub-mode so the Projects tab always lands on the clean list.
    //
    // Re-clicking the tab you're ALREADY in returns to that section's LIST,
    // even from a detail sub-view (/generations/:id, /catalog/:id): before this
    // the click kept the open id, so pathFor produced the current path and
    // pushPath no-op'd — clicking "Generations" while inside a generation did
    // nothing. Clear the id slot so we navigate to the section root. A
    // cross-section click still restores that section's last-open id
    // (idForSection), preserving the original "pick up where you left off".
    const reclickingActive = sectionId === activeSection;
    selectedRunId = null;
    rulesMode = false;
    rulesFormMode = null;
    rulesEditRuleId = null;
    projectMode = null;
    projectEditId = null;
    templateMode = null;
    templateEditId = null;
    if (reclickingActive) {
      if (sectionId === "generations") selectedGenerationId = null;
      if (sectionId === "catalog") selectedProjectId = null;
      if (sectionId === "help") helpTopic = null;
    }
    activeSection = sectionId;
    navigate(sectionId, reclickingActive ? null : idForSection(sectionId));
  }

  // Projects section: open/close the create/edit/upload forms through the URL so
  // the browser Back button closes them (each open is a history entry) and they
  // deep-link. Mirrors the generations open/close contract.
  function openProjectNew() {
    projectMode = "new";
    projectEditId = null;
    templateMode = null;
    templateEditId = null;
    pushPath("/projects/new", { section: "projects", projectMode: "new" });
  }
  function openProjectEdit(id) {
    projectMode = "edit";
    projectEditId = id;
    templateMode = null;
    templateEditId = null;
    pushPath(`/projects/edit/${id}`, { section: "projects", projectMode: "edit", projectEditId: id });
  }
  function openTemplateNew() {
    templateMode = "new";
    templateEditId = null;
    projectMode = null;
    projectEditId = null;
    pushPath("/projects/templates/new", { section: "projects", templateMode: "new" });
  }
  function openTemplateEdit(id) {
    templateMode = "edit";
    templateEditId = id;
    projectMode = null;
    projectEditId = null;
    pushPath(`/projects/templates/edit/${id}`, { section: "projects", templateMode: "edit", templateEditId: id });
  }
  function closeProjectsForm() {
    projectMode = null;
    projectEditId = null;
    templateMode = null;
    templateEditId = null;
    pushPath("/projects", { section: "projects" });
  }

  // Callbacks handed to GenerationManager: it asks us to change the URL, we own it.
  function openGeneration(id) {
    // Changing (or opening) a generation drops any previously-selected run so the
    // URL stays clean at /generations/:id until the user explicitly picks a run.
    selectedRunId = null;
    rulesMode = false;
    rulesFormMode = null;
    rulesEditRuleId = null;
    selectedGenerationId = id;
    navigate("generations", id);
  }

  function closeGeneration() {
    selectedRunId = null;
    rulesMode = false;
    rulesFormMode = null;
    rulesEditRuleId = null;
    selectedGenerationId = null;
    navigate("generations", null);
  }

  // GenerationManager asks us to select (or clear) a run; we own the URL.
  // runId=null returns to the clean /generations/:id path ("Current run").
  // `replace` (no history entry) is used when the panel REFLECTS its live run in
  // the address bar (auto-attach) rather than the user explicitly picking a run.
  function selectRun(runId, { replace = false } = {}) {
    if (!selectedGenerationId) {
      return;
    }
    selectedRunId = runId;
    navigate("generations", selectedGenerationId, { runId, replace });
  }

  // Part C: RuleEditor's "Edit rules" button asks us to open the wide rules
  // page for the currently-selected generation; a real history entry (Back
  // returns to the generation detail/rail), URL-addressable at
  // /generations/:id/rules. Mutually exclusive with a selected run, so drop it.
  function openRules() {
    if (!selectedGenerationId) {
      return;
    }
    selectedRunId = null;
    rulesMode = true;
    rulesFormMode = null;
    rulesEditRuleId = null;
    navigate("generations", selectedGenerationId, { rules: true });
  }

  // RulesPage's Back affordance returns to the plain generation detail path.
  function closeRules() {
    if (!selectedGenerationId) {
      return;
    }
    rulesMode = false;
    rulesFormMode = null;
    rulesEditRuleId = null;
    navigate("generations", selectedGenerationId);
  }

  // RulesPage asks us to open its create/edit form as its own URL (Back closes
  // it → returns to /generations/:id/rules). Mirrors openProjectNew/Edit.
  function openRuleNew() {
    if (!selectedGenerationId) return;
    rulesMode = true;
    rulesFormMode = "new";
    rulesEditRuleId = null;
    pushPath(rulesPathFor(selectedGenerationId, "new"), { section: "generations", id: selectedGenerationId, rulesFormMode: "new" });
  }
  function openRuleEdit(ruleId) {
    if (!selectedGenerationId || !ruleId) return;
    rulesMode = true;
    rulesFormMode = "edit";
    rulesEditRuleId = ruleId;
    pushPath(rulesPathFor(selectedGenerationId, "edit", ruleId), { section: "generations", id: selectedGenerationId, rulesFormMode: "edit", rulesEditRuleId: ruleId });
  }
  function closeRuleForm() {
    if (!selectedGenerationId) return;
    rulesFormMode = null;
    rulesEditRuleId = null;
    pushPath(rulesPathFor(selectedGenerationId), { section: "generations", id: selectedGenerationId });
  }

  // Same contract for CatalogManager's project picker.
  function selectProject(id) {
    selectedProjectId = id;
    navigate("catalog", id);
  }

  // Same contract for HelpManager's topic list: selecting a topic asks us to
  // navigate to /help/:slug (Back returns to the list/prev topic).
  function selectHelpTopic(slug) {
    helpTopic = slug;
    navigate("help", slug);
  }

  onMount(() => {
    applyLocation();
    // Canonicalise the address bar (e.g. "/" -> "/generations") without a history
    // entry. Preserve a deep-linked run id / rules sub-page (generations) and the
    // projects sub-mode path so an F5 on /generations/:id/runs/:runId,
    // /generations/:id/rules, or /projects/new keeps it.
    let canonical;
    if (activeSection === "projects") {
      canonical = projectsPathFor(projectMode, projectEditId, templateMode, templateEditId);
    } else if (activeSection === "generations" && rulesMode && selectedGenerationId) {
      // Preserve a deep-linked rules form sub-mode (.../rules/new, .../rules/:rid/edit).
      canonical = rulesPathFor(selectedGenerationId, rulesFormMode, rulesEditRuleId);
    } else {
      canonical = pathFor(activeSection, idForSection(activeSection), activeSection === "generations" ? selectedRunId : null, false);
    }
    pushPath(canonical, { section: activeSection }, true);

    const onPopState = () => applyLocation();
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  });
</script>

<div class="w-full max-w-[128rem] mx-auto min-h-screen p-3 sm:p-5 md:p-6 lg:p-8 box-border font-sans">
  <Header {sections} {activeSection} {userEmail} {userRole} {csrfToken} onSelect={selectSection} />

  <section class="bg-white border border-neutral-200 rounded-xl p-4 sm:p-5 md:p-6 shadow-xs">
    {#if activeSection === "generations" && rulesMode && selectedGenerationId}
      <!-- Part C: the wide, URL-addressable rules edit page — a sub-view of the
           generations section (mirrors how a run archive is a sub-view), so the
           "Generations" tab stays active while this renders instead of
           GenerationManager's detail view. -->
      <RulesPage
        generationId={selectedGenerationId}
        {isAdmin}
        formMode={rulesFormMode}
        editRuleId={rulesEditRuleId}
        onClose={closeRules}
        onOpenNew={openRuleNew}
        onOpenEdit={openRuleEdit}
        onCloseForm={closeRuleForm}
      />
    {:else if activeSection === "generations"}
      <GenerationManager {isAdmin} {selectedGenerationId} {selectedRunId} onOpen={openGeneration} onClose={closeGeneration} onSelectRun={selectRun} onEditRules={openRules} />
    {:else if activeSection === "pat_tokens"}
      <PatTokenManager />
    {:else if activeSection === "projects"}
      <h2 class="text-base font-bold text-neutral-800 mb-4">Projects &amp; Templates</h2>
      <ProjectManager
        {isAdmin}
        {projectMode}
        {projectEditId}
        {templateMode}
        {templateEditId}
        onProjectNew={openProjectNew}
        onProjectEdit={openProjectEdit}
        onProjectsClose={closeProjectsForm}
        onTemplateNew={openTemplateNew}
        onTemplateClose={closeProjectsForm}
        onTemplateEdit={openTemplateEdit}
      />
    {:else if activeSection === "catalog"}
      <h2 class="text-base font-bold text-neutral-800 mb-4">Catalog</h2>
      <CatalogManager {isAdmin} {selectedProjectId} onSelectProject={selectProject} />
    {:else if activeSection === "users"}
      <h2 class="text-base font-bold text-neutral-800 mb-4">Users</h2>
      <UserManager {isAdmin} />
    {:else if activeSection === "settings/llm"}
      <h2 class="text-base font-bold text-neutral-800 mb-4">LLM Settings</h2>
      <LlmSettingsManager {isAdmin} />
    {:else if activeSection === "help"}
      <HelpManager selectedTopic={helpTopic} onSelectTopic={selectHelpTopic} />
    {/if}
  </section>
</div>
